import React from "react";
import { connect } from "react-redux";
import { Card, Row, Col, Form, Table } from "react-bootstrap";
import PropTypes from "prop-types";

import ConditionalButton from "../../../layout/conditional-button";
import ErrorModal from "../../../layout/modal";
import DeleteModal from "../../../layout/modal";
import { loading, send as pageInfo } from "../../../actions/page";
import { get as getSettings} from "../../../actions/settings";
import { get as loadPayrollCheck, save as savePayrollCheck, destroy as destroyPayrollCheck } from "../../../actions/pay.checks";
import { markPrinted } from "../../../actions/check.register";
import { get as getAddress } from "../../../actions/addresses";
import { flagged as getReports, render } from "../../../actions/report.template";
import { withNavigation } from "../../../utils/wrapper";
import { downloadCheckPdf } from "../../../utils/checks";
import { isGuid } from "../../../utils/string";

const safeParseJson = (value, fallback = {}) => {
  try {
    return typeof value === "string" ? JSON.parse(value) : (value || fallback);
  } catch (_err) {
    return fallback;
  }
};

class EarningsStatement extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      uuid: null,
      checkNumber: null,
      payDate: "",

      // optional: keep the loaded record here for rendering
      item: null,
      modal: {
        error: { show: false },
        delete: { show: false },
      },
    };
  }

  componentDidMount = async () => {
    await this.props.pageInfo({ title: "Earnings Statement", breadcrumb: "Earnings Statement" });
    await this.loadData();
  };

  componentDidUpdate(prevProps) {
    // if route param uuid changes, reload
    const prevUuid = prevProps?.params?.uuid;
    const nextUuid = this.props?.params?.uuid;
    if (prevUuid !== nextUuid) this.loadData();
  }

  get uuid() {
    return this.props?.params?.uuid;
  }


  loadData = async () => {
    const uuid = this.uuid ?? null;
    if (!uuid) return;
    const isExisting = isGuid(uuid);

    this.props.loading(true);
    try {
      const item = isExisting ? await this.props.loadPayrollCheck({ uuid }) : { item: null };

      const reports = await this.props.getReports({ flags: 1024+2048 });
      const [firm, accountsSetting] = await Promise.all([
        this.props.getSettings({ authentic: true, category: "Firm" }),
        this.props.getSettings({ authentic: true, name: "ACCOUNTS" }),
      ]);
      const addresses = safeParseJson(firm?.FIRM_LOCATIONS, []);
      for (let i = 0; i < addresses.length; i++) {
        const uuid = addresses[i];
        const address = await this.props.getAddress({ uuid });
        addresses[i] = { ...address };
      }

      // Support either: action returns record OR {item: record}
      const rec = item?.item ?? item ?? null;

      this.setState({
        uuid,
        item: rec,
        checkNumber: rec?.checkNumber ?? null,
        payDate: rec?.payDate ?? "",
        reports: [...reports],
        firm,
        accounts: safeParseJson(accountsSetting?.ACCOUNTS || accountsSetting?.value || accountsSetting, {}),
        addresses
      });
    } finally {
      this.props.loading(false);
    }
  };

  onChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  onSave = async () => {
    const { uuid, checkNumber, payDate } = this.state;

    this.props.loading(true);
    try {
      const saved = await this.props.savePayrollCheck({
        ...(isGuid(uuid) ? { uuid } : {}),
        checkNumber: (checkNumber ?? "").trim() === "" ? null : checkNumber,
        payDate: (payDate ?? "").trim() === "" ? null : payDate,
      });

      if (!isGuid(uuid) && isGuid(saved?.uuid)) {
        this.props.navigate(`/dashboard/accounting/payroll/${saved.uuid}`);
        return;
      }

      // reload from server so UI stays canonical
      await this.loadData();
    } finally {
      this.props.loading(false);
    }
  };

  onDeleteClick = () => {
    if (!isGuid(this.state.uuid)) return;

    const label = this.state.checkNumber || this.state.item?.checkNumber || this.state.uuid;
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        delete: {
          show: true,
          title: "Delete payroll check",
          body: `This action will permanently delete payroll check ${label}.`,
        },
      },
    }));
  };

  onDeleteOkModal = async () => {
    const uuid = this.state.uuid;
    if (!isGuid(uuid)) return;

    this.props.loading(true);
    try {
      const response = await this.props.destroyPayrollCheck(uuid);
      if (!response?.success) {
        this.setState((prev) => ({
          modal: {
            ...prev.modal,
            delete: { show: false },
            error: {
              show: true,
              title: "Delete payroll check",
              body: response?.error?.message || "The payroll check could not be deleted.",
            },
          },
        }));
        return;
      }

      this.props.navigate("/dashboard/accounting/payroll");
    } finally {
      this.props.loading(false);
    }
  };

  onDeleteHideModal = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        delete: { show: false },
      },
    }));
  };

  onErrorHideModal = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        error: { show: false },
      },
    }));
  };

  onDownloadCheck = async () => {
    const item = this.state.item || {};
    const payDate = this.state.payDate || item.payDate;
    const checkNumber = this.state.checkNumber || item.checkNumber || "";
    const amount = Number(item.netPay ?? item.meta?.results?.netPay ?? 0);

    await downloadCheckPdf({
      render: this.props.render,
      templateUuid: isGuid(this.state.accounts?.payroll?.check)
        ? this.state.accounts.payroll.check
        : this.state.accounts?.general?.check,
      check: {
        ...item,
        amount,
        checkDate: payDate,
        checkNumber,
        payeeName: item.payeeName ?? item.employeeName ?? "",
        memo: "Payroll check",
        referenceNumber: checkNumber,
      },
    });

    await this.props.markPrinted({
      journalEntryUuid: item.journalEntryUuid,
      checkNumber,
      checkDate: payDate,
    });
  };

  renderHeader = () => {
    const companyName = this.state?.firm?.FIRM_LEGAL_NAME ?? "";
    const address = this.state?.addresses?.[0] || {};
    const companyAddress = [address.line1, address.line2, [address.city, address.state, address.zipCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");

    return (
      <Row className="align-items-start">
        <Col md={8}>
          <div className="fw-bold">{companyName}</div>
          <div className="text-muted">{companyAddress}</div>
        </Col>
        <Col md={4} className="text-end">
          <div className="fw-bold fs-5"></div>
        </Col>
      </Row>
    );
  };

  renderEmployeeStrip = () => {
    const item = this.state.item || {};

    const employeeName = item.payeeName ?? item.employeeName ?? "—";
    const ssn = item.ssnLast4 ? `***-**-${item.ssnLast4}` : (item.ssnMasked ?? "—");
    const employeeId = item.employeeId ?? item.staffId ?? "—";

    const payPeriod = item.startsOn && item.endsOn ? `${item.startsOn} - ${item.endsOn}` : "—";

    const displayCheckNo =
      (this.state.checkNumber ?? "").trim() === "" ? "[unassigned]" : this.state.checkNumber;

    return (
      <>
        <Row className="mt-3 border-top border-bottom py-2" style={{ background: "#f4f4f4" }}>
          <Col className="text-uppercase small fw-bold">Employee Name</Col>
          <Col className="text-uppercase small fw-bold">SSN</Col>
          <Col className="text-uppercase small fw-bold">Employee ID</Col>
          <Col className="text-uppercase small fw-bold">Check No.</Col>
          <Col className="text-uppercase small fw-bold">Pay Period</Col>
          <Col className="text-uppercase small fw-bold text-end">Pay Date</Col>
        </Row>

        <Row className="py-2 border-bottom">
          <Col>{employeeName}</Col>
          <Col>{ssn}</Col>
          <Col>{employeeId}</Col>

          <Col>
            <Form.Control
              size="sm"
              name="checkNumber"
              value={this.state.checkNumber ?? ""}
              placeholder="[unassigned]"
              onChange={this.onChange}
            />
            <div className="small text-muted mt-1">Current: {displayCheckNo}</div>
          </Col>

          <Col>{payPeriod}</Col>

          <Col className="text-end">
            <Form.Control
              size="sm"
              type="date"
              name="payDate"
              value={this.state.payDate ?? ""}
              onChange={this.onChange}
            />
          </Col>
        </Row>
      </>
    );
  };

  renderBodyTable = () => {
    const item = this.state.item || {};
    const results = item.results ?? item.meta?.results ?? {};

    const rate = results?.earnings?.hourlyRate ?? item.hourlyRate ?? item.payRate ?? 0;
    const hours = results?.hours?.totalHours ?? item.hoursWorked ?? 0;
    const gross = results?.earnings?.grossPay ?? item.grossPay ?? 0;

    const taxItems =
      results?.taxes?.items ??
      item.taxes?.items ??
      item.meta?.taxes ??
      [];

    const normalizedTaxes = Array.isArray(taxItems) ? taxItems : [];

    return (
      <Row className="mt-3">
        <Col md={6}>
          <div className="text-uppercase small fw-bold border-bottom pb-1">Income</div>
          <Table bordered size="sm" className="mt-2">
            <thead>
              <tr>
                <th className="text-uppercase small">Income</th>
                <th className="text-uppercase small text-end">Rate</th>
                <th className="text-uppercase small text-end">Hours</th>
                <th className="text-uppercase small text-end">Current Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Gross Wages</td>
                <td className="text-end">{Number(rate).toFixed(2)}</td>
                <td className="text-end">{Number(hours).toFixed(2)}</td>
                <td className="text-end">{Number(gross).toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>
        </Col>

        <Col md={6}>
          <div className="text-uppercase small fw-bold border-bottom pb-1">Deductions / Taxes</div>
          <Table bordered size="sm" className="mt-2">
            <thead>
              <tr>
                <th className="text-uppercase small">Description</th>
                <th className="text-uppercase small text-end">Current Total</th>
              </tr>
            </thead>
            <tbody>
              {normalizedTaxes.length === 0 ? (
                <tr>
                  <td colSpan={2} className="text-muted">
                    No tax items available.
                  </td>
                </tr>
              ) : (
                normalizedTaxes.map((t, idx) => (
                  <tr key={`tax.${idx}.${t.code || "x"}`}>
                    <td>{t.caption ?? t.code ?? "Tax"}</td>
                    <td className="text-end">{Number(t.amount ?? 0).toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
    );
  };

  renderTotals = () => {
    const item = this.state.item || {};
    const results = item.results ?? item.meta?.results ?? {};

    const ytdGross = item.ytdGross ?? item.meta?.ytd?.gross ?? 0;
    const ytdDeductions = item.ytdDeductions ?? item.meta?.ytd?.deductions ?? 0;
    const ytdNet = item.ytdNet ?? 0;

    const gross = results?.earnings?.grossPay ?? item.grossPay ?? 0;
    const taxes = results?.taxes?.total ?? item.totalTaxes ?? 0;
    const deductions = results?.deductions?.total ?? item.totalDeductions ?? 0;
    const net = results?.netPay ?? item.netPay ?? (Number(gross) - Number(taxes) - Number(deductions));

    return (
      <Row className="mt-3">
        <Col>
          <Table bordered size="sm">
            <thead>
              <tr>
                <th className="text-uppercase small">YTD Gross</th>
                <th className="text-uppercase small">YTD Deductions</th>
                <th className="text-uppercase small">YTD Net Pay</th>
                <th className="text-uppercase small">Current Total</th>
                <th className="text-uppercase small">Current Deductions</th>
                <th className="text-uppercase small">Net Pay</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{Number(ytdGross+gross).toFixed(2)}</td>
                <td>{Number(ytdDeductions+deductions+taxes).toFixed(2)}</td>
                <td>{Number(ytdNet+net).toFixed(2)}</td>
                <td>{Number(gross).toFixed(2)}</td>
                <td>{Number(taxes + deductions).toFixed(2)}</td>
                <td>{Number(net).toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>
    );
  };

  render() {
    return (<>
      <Col md="8" className="float-right text-end">
        <ConditionalButton enabledVariant="outline-primary" tooltip="Save Changes" display={true} enabled={true} onEnabledClick={this.onSave} icon="save">Save</ConditionalButton>
        <ConditionalButton enabledVariant="outline-primary" tooltip="Download Check PDF" display={true} enabled={true} onEnabledClick={this.onDownloadCheck} icon="download">Check PDF</ConditionalButton>
        <ConditionalButton enabledVariant="outline-danger" tooltip="Delete payroll" display={isGuid(this.state.uuid)} enabled={true} onEnabledClick={this.onDeleteClick} icon="bin">Delete</ConditionalButton>
      </Col>
      <Card className="p-3">
        {this.renderHeader()}
        {this.renderEmployeeStrip()}
        {this.renderBodyTable()}
        {this.renderTotals()}
      </Card>

      <ErrorModal
        show={this.state.modal.error.show}
        title={this.state.modal.error.title}
        body={this.state.modal.error.body}
        buttons={[
          {
            default: true,
            caption: "ok",
            onEnabledClick: this.onErrorHideModal,
            variant: "secondary",
            display: true,
            enabled: true,
          },
        ]}
      ></ErrorModal>

      <DeleteModal
        show={this.state.modal.delete.show}
        title={this.state.modal.delete.title}
        body={this.state.modal.delete.body}
        buttons={[
          {
            caption: "ok",
            onEnabledClick: this.onDeleteOkModal,
            variant: "primary",
            display: true,
            enabled: true,
          },
          {
            default: true,
            caption: "cancel",
            onEnabledClick: this.onDeleteHideModal,
            variant: "secondary",
            display: true,
            enabled: true,
          },
        ]}
      ></DeleteModal>
    </>
    );
  }
}

EarningsStatement.propTypes = {
  auth: PropTypes.object,
  params: PropTypes.object, // withNavigation injects params
  loadPayrollCheck: PropTypes.func.isRequired,
  savePayrollCheck: PropTypes.func.isRequired,
  destroyPayrollCheck: PropTypes.func.isRequired,
  markPrinted: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withNavigation(connect(mapStateToProps, {
  loading,
  pageInfo,
  loadPayrollCheck,
  savePayrollCheck,
  destroyPayrollCheck,
  getSettings,
  getReports,
  getAddress,
  render,
  markPrinted
})(EarningsStatement));
